import { useState } from 'react';
import { Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
  index: number;
  onOpen: (product: Product) => void;
}

const categoryConfig: Record<string, { color: string }> = {
  strategic: { color: '#FF6B35' },
  foundation: { color: '#00D4FF' },
  marketing: { color: '#A855F7' },
  fishery: { color: '#10B981' },
};

export default function ProductCard({ product, index, onOpen }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const color = categoryConfig[product.category]?.color || '#00D4FF';

  return (
    <button
      onClick={() => onOpen(product)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group text-left cursor-pointer relative block rounded-[24px] border bg-white/5 backdrop-blur-xl p-6 overflow-hidden transition-all duration-500 w-full hover:-translate-y-2.5 hover:scale-[1.02]"
      style={{
        borderColor: isHovered ? `${color}40` : 'rgba(255,255,255,0.1)',
        boxShadow: isHovered ? `0 0 50px ${color}20` : `0 0 20px ${color}08`,
      }}
    >
      {/* 状态标识 */}
      <div
        className="absolute right-5 top-5 rounded-full border px-3 py-1 text-xs transition-colors duration-300"
        style={{
          borderColor: isHovered ? `${color}30` : 'rgba(255,255,255,0.1)',
          color: isHovered ? color : 'rgba(255,255,255,0.5)',
        }}
      >
        {product.status || '已上线'}
      </div>

      {/* 图标 + 编号 */}
      <div className="flex items-center gap-4 mb-5">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300"
          style={{
            backgroundColor: `${color}15`,
            boxShadow: isHovered ? `0 0 25px ${color}40` : `0 0 15px ${color}20`,
          }}
        >
          <i className={`${product.icon} text-2xl`} style={{ color }}></i>
        </div>
        <div>
          <div className="text-xs tracking-[0.35em] mb-2" style={{ color }}>
            PRODUCT {String(index + 1).padStart(2, '0')}
          </div>
          <div className="text-xs text-white/45">{product.layer}</div>
        </div>
      </div>

      {/* 名称 */}
      <div className="text-xl md:text-2xl font-semibold mb-3 text-white leading-snug">
        {product.name}
      </div>

      {/* Slogan */}
      <div className="text-white/70 leading-7 min-h-[56px] text-sm md:text-base">
        {product.slogan}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-4">
        {product.tags?.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="rounded-full px-3 py-1 text-xs text-white/65 border"
            style={{
              backgroundColor: `${color}10`,
              borderColor: `${color}15`,
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Highlights */}
      <div className="mt-4 space-y-2">
        {product.highlights.slice(0, 3).map((point) => (
          <div key={point} className="flex gap-2 text-sm text-white/55">
            <span
              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
              style={{ backgroundColor: `${color}70` }}
            />
            <span>{point}</span>
          </div>
        ))}
      </div>

      {/* 底部 */}
      <div className="mt-5 flex items-center justify-between text-sm" style={{ color }}>
        <span>查看产品档案</span>
        <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
      </div>

      {/* 装饰光效 */}
      <div
        className="absolute -right-10 -top-10 w-32 h-32 blur-3xl rounded-full pointer-events-none transition-opacity duration-500"
        style={{
          backgroundColor: color,
          opacity: isHovered ? 0.25 : 0.08,
        }}
      />
    </button>
  );
}